import { useConfigContext } from '@fe-core/context';
import {
  FaIcon,
  ListItem,
  QuizSurveyModal,
  Resource,
  ResourceTexts,
  TResource,
  useMarkResourceAsRead,
} from '@fe-learner-common';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import * as Dialog from '@radix-ui/react-dialog';
import { FC } from 'react';
import { buildResourceAriaLabel } from './buildResourceAriaLabel';
import { ResourceActions } from './ResourceActions';

type QuizSurveyProps = {
  resource: TResource;
  participationId: string;
  texts: ResourceTexts;
  container: HTMLElement;
  onDescriptionClick: (resourceId: string) => void;
};

export const QuizSurvey: FC<QuizSurveyProps> = ({
  resource,
  participationId,
  texts,
  container,
  onDescriptionClick,
  ...rest
}) => {
  const { config } = useConfigContext();
  const { mutate: markAsRead } = useMarkResourceAsRead({
    resourceId: resource.rid,
    participationId,
  });
  const url =
    resource.link ||
    `${config.apiUrls.aleweb}/participation/${participationId}/resource/${resource.rid}/useDirect`;

  const typeText =
    texts.groupTypes[
      resource.componentType === 'material-scorequiz' ? 'quiz' : 'survey'
    ];

  const Trigger = () => (
    <ListItem>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="absolute inset-0 z-0 rounded-lg"
          onClick={() => {
            markAsRead();
          }}
          aria-label={buildResourceAriaLabel({
            type: typeText,
            title: resource.material.webTitle,
            tags: resource.tags,
          })}
          {...rest}
        />
      </Dialog.Trigger>
      <div className="flex flex-1 items-start justify-between gap-x-4 p-3">
        <Resource
          resourceId={resource.rid}
          title={resource.material.webTitle}
          groupType={resource.groupType}
          thumbnail={resource.material.thumbnail}
          tags={resource.tags}
          isNew={resource.newResource}
          texts={texts}
        />
        <ResourceActions
          resourceId={resource.rid}
          hasDescription={!!resource.material.webDescription}
          onDescriptionClick={onDescriptionClick}
          descriptionAriaLabel={texts.descriptionAriaLabel}
        >
          <Dialog.Trigger asChild>
            <button
              type="button"
              className="l-btn l-btn-sm l-btn-tertiary l-icon-btn hover:bg-neutral-50"
              onClick={() => {
                markAsRead();
              }}
              aria-label={texts.viewResourceAriaLabel}
            >
              <FaIcon width={16} height={16} icon={faArrowRight} />
            </button>
          </Dialog.Trigger>
        </ResourceActions>
      </div>
    </ListItem>
  );

  return (
    <QuizSurveyModal
      container={container}
      title={typeText}
      Trigger={Trigger}
      src={url}
    />
  );
};
